import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ClickTooltip } from '../../../../../../base/components/ClickTooltip';
import { ErrorMessage } from '../../../../../../base/components/Forms/ErrorMessage';
import { PasswordInput } from '../../../../../../base/components/Forms/PasswordInput';
import { PhoneInput } from '../../../../../../base/components/Forms/PhoneInput';
import { TermsCheckbox } from '../../../../../../base/components/Forms/TermsCheckbox';
import { Icon } from '../../../../../../base/components/Icon';
import { useAppDispatch, useAppSelector, useRoutesWithLang } from '../../../../../../base/hooks';
import { getRefById, RefMap, scrollToError } from '../../../../../../base/utils';
import { __t } from '../../../../../../base/utils/t';
import { signUpActions } from '../../../redux/slices/signUpSlice';
import { AuthRoutes } from '../../../routes';
import { AuthRoutes as RecruiterAuthRoutes } from '../../../../../../recruiter/units/authorization/routes';

import './styles.scss';

export const RegistrationInfo = () => {
    const dispatch = useAppDispatch();
    const {
        fields: { phone, password, agreeTerms },
        errors,
        shouldScrollToError,
    } = useAppSelector((state) => state.workerAppSignUp);
    const { phoneInputConfig } = useAppSelector((state) => state.workerApp.config);
    const authRoutes = useRoutesWithLang<typeof AuthRoutes>(AuthRoutes);
    const recruiterAuthRoutes = useRoutesWithLang<typeof RecruiterAuthRoutes>(RecruiterAuthRoutes);

    const refMap: RefMap[] = [
        {
            id: 'phone',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'password',
            ref: useRef<HTMLDivElement>(null),
        },
        {
            id: 'terms',
            ref: useRef<HTMLDivElement>(null),
        },
    ];

    useEffect(() => {
        if (errors && shouldScrollToError) {
            scrollToError(errors, refMap);
            dispatch(signUpActions.setShouldScrollToError(false));
        }
    }, [refMap, errors, shouldScrollToError]);

    const inputChangeHandler = (id: string, value: unknown) => {
        dispatch(signUpActions.setField({ id, value }));
    };

    return (
        <div className='worker-signup__form-wrapper'>
            <div
                className='worker-signup__form-item scroll-margin-for-fixed-header'
                ref={getRefById('phone', refMap)?.ref}
            >
                <p className='worker-signup__form-label'>{__t('Телефон')}</p>
                <ClickTooltip
                    activeButton={
                        <PhoneInput
                            value={phone}
                            onChange={(value: string) => inputChangeHandler('phone', value)}
                            errors={errors?.phone}
                            onlyCountries={phoneInputConfig.countries}
                            preferredCountries={phoneInputConfig.preferredCountries}
                            localization={phoneInputConfig.localization}
                            tabIndex={1}
                        />
                    }
                    align='right'
                    hideCloseIcon={true}
                    className='worker-signup__input-tooltip-container'
                    wrapperClassName='worker-signup__input-tooltip-wrapper'
                >
                    <div className='worker-signup__input-tooltip'>
                        <p className='worker-signup__input-tooltip-title'>
                            {__t('Номер телефона используется для входа на сайт')}
                        </p>{' '}
                        <p className='worker-signup__input-tooltip-text'>
                            {__t('Вы никогда не будете получать спам от нашего сервиса.')}
                        </p>
                    </div>
                </ClickTooltip>
            </div>

            <div
                className='worker-signup__form-item scroll-margin-for-fixed-header'
                ref={getRefById('password', refMap)?.ref}
            >
                <p className='worker-signup__form-label'>{__t('Придумайте пароль')}</p>
                <PasswordInput
                    value={password}
                    placeholder={__t('Минимум 6 символов')}
                    onChange={(value: string) => inputChangeHandler('password', value)}
                    className='worker-signup__form-input'
                    errors={errors?.password}
                    tabIndex={2}
                />
            </div>

            <div
                className='worker-signup__form-item scroll-margin-for-fixed-header worker-signup__terms'
                ref={getRefById('terms', refMap)?.ref}
            >
                <TermsCheckbox
                    id='agreeTerms'
                    checked={agreeTerms}
                    onChange={inputChangeHandler}
                    hasError={!!errors?.agreeTerms?.length}
                >
                    <>
                        {__t('Я принимаю ')}
                        <a href='/pages/default/view?page=terms-and-conditions' target='_blank'>
                            {__t('правила и условия сервиса')}
                        </a>
                        {__t(' и ')}
                        <a href='/pages/default/view?page=privacy-policy' target='_blank'>
                            {__t('политику конфиденциальности')}
                        </a>
                    </>
                </TermsCheckbox>
                <ErrorMessage errors={errors?.agreeTerms} />
            </div>

            <p className='worker-signup__login-link'>
                {__t('Уже зарегистрированы?')}&nbsp;
                <Link to={authRoutes.Login} state={{ from: { pathname: authRoutes.Login } }}>
                    {__t('Войдите')}
                </Link>
            </p>

            <a
                href={`${window.GLOBALS?.crossSiteBaseUrl}${recruiterAuthRoutes.SignUpIndex}`}
                className='worker-signup__recruiters-link'
            >
                <Icon icon='user-tie-hair-solid' color='highlight' className='worker-signup__recruiters-link-icon' />
                {__t('Я рекрутер')}
            </a>
        </div>
    );
};
